import moment from "moment";
import { intersectKeys, explodeInnerArrays, validateAndSort } from "@/utils/transformers";
import { ANALYTICS_TABLE_CELL_HEIGHT, EVENTS } from "@/utils/constants";
import { mapGetters } from "vuex";
import { DATE_PICKER_COMPARE_TITLES, DATE_PICKER_RANGE_TITLES, getDatePickerUIObjectFromDateRange, DATE_RANGES_FROM_SELECTOR, getPeriodGroupFromCurrentPeriod, getDatePickerRangeCompare, CUSTOM_COMPARE_FUNCTION } from "@/utils/datepicker";
import { PERIOD_OPTIONS } from "../utils/constants";
import { DATE_PICKER_COMPARE_SWITCHES } from "../utils/datepicker";
export default {
  name: "LiteTableMixin",
  data() {
    return {
      evolCategory: {
        [PERIOD_OPTIONS.PREV_PERIOD.value]: "",
        [PERIOD_OPTIONS.SAME_PERIOD_LY.value]: "last_year_"
      },
      evolutionType: "percentage",
      evolutionTypeKpi: {
        percentage: "",
        currency: "_currency"
      },
      datePickerStorageDispatcher: null
    };
  },
  computed: {
    ...mapGetters(["getSystemWeekType"]),
    in_currency() {
      return this.$store.getters?.currencyConfigInCurrencyGet() ?? "";
    },
    cssProps() {
      return ANALYTICS_TABLE_CELL_HEIGHT[this.groupBy] ?? {
        "--lite-table-td-height": "32px"
      };
    },
    datePickerCompareTitle() {
      return category => DATE_PICKER_COMPARE_SWITCHES[category] ?? DATE_PICKER_COMPARE_TITLES.PRECEDING_PERIOD;
    },
    showParentViewAll() {
      return (parentsMap, item, key, limit) => {
        // !competitor_parents.get(row.item?.brand)?.view_all || competitor_parents.get(row.item?.brand)?.children_length > 5

        const parent = parentsMap.get(item[key]);
        const viewAllFalse = !parent?.view_all;
        const childrenLengthIsGreaterThanLimit = parent.children_length > limit;
        return viewAllFalse && childrenLengthIsGreaterThanLimit;
      };
    }
  },
  methods: {
    getPeriodGroupFromCurrentPeriod(currentPeriod, period) {
      return getPeriodGroupFromCurrentPeriod(currentPeriod, period, this.getSystemWeekType);
    },
    handleRowClass(item, items) {
      const lastItem = items[items.length - 1];
      const baseClass = item.selected === undefined ? "" : item.selected ? "lite-table-selected-row" : "lite-table-unselected-row";
      const lastClass = item === lastItem ? "last" : "";
      return [baseClass, lastClass].filter(Boolean).join(" ");
    },
    handleRowClicked(item, reset, selected, key, items) {
      let elementItems = "items" in this ? this.items : items;
      if (this.loading) return;
      if (item.selected) {
        elementItems.forEach(item => {
          item.selected = undefined;
        });
        if (reset) reset();
      } else {
        elementItems.forEach(item => {
          item.selected = false;
        });
        item.selected = true;
        selected(!key ? item : item[key]);
      }
    },
    async handleBreakdownRowClicked(item, reset, selected, items) {
      let elementItems = "items" in this ? this.items : items;
      if (this.loading) return;
      if (item.selected) {
        elementItems.forEach(item => {
          item.selected = undefined;
        });
        if (reset) await reset();
      } else {
        elementItems.forEach(item => {
          item.selected = false;
        });
        item.selected = true;
        await selected(item);
      }
    },
    handleSort({
      sort,
      direction
    }) {
      // Check if there is a `noSortColumns` columns exits
      if ("noSortColumns" in this.$data && this.$data.noSortColumns.includes(sort)) {
        window.$bus.trigger("showWarning", {
          message: "Cannot sort by this field",
          visible: true,
          delay: 1
        });
        return;
      }
      this.$emit("handleSort", {
        sort,
        direction
      });
    },
    validateAndHandleSort(sortData) {
      try {
        const sampleRow = this.filteredItems?.[0] ?? this.items?.[0] ?? null;
        validateAndSort(sortData, this.fields, data => this.handleSort(data), sampleRow);
      } catch (error) {
        window.$bus.trigger("showWarning", {
          message: error.message,
          visible: true,
          delay: 1
        });
      }
    },
    activateDateValueStyles(row) {
      return row.item.selected ? {
        color: "#312783",
        fontWeight: "600"
      } : {};
    },
    prepareCsvRowsForDownload(rows, csv_headers) {
      return explodeInnerArrays(rows).map(row => intersectKeys(row, csv_headers));
    },
    async downloader(page = 1, page_size, csv_headers, fetcher) {
      const response = await this.$store.dispatch(fetcher, {
        pager: {
          page,
          page_size
        },
        store: false
      });
      if (response) {
        const {
          report
        } = response;
        let {
          result
        } = report;
        return this.prepareCsvRowsForDownload(result, csv_headers);
      }
      return [];
    },
    handleSetLiteTablePeriod(date, period, callback, compareTitle) {
      if (!date) {
        callback(date);
        return;
      }
      const selectedPeriodData = DATE_RANGES_FROM_SELECTOR[period](date, this.getSystemWeekType);
      const range = selectedPeriodData.prev_period.range;
      const compare_prev_period = selectedPeriodData.prev_period.compare;
      const compare_last_year = selectedPeriodData.same_period_ly.compare;

      // Determine which comparison to use for display based on compareTitle
      let compare;
      if (compareTitle === DATE_PICKER_COMPARE_TITLES.SAME_PERIOD_LY) {
        compare = compare_last_year;
      } else {
        // Default to previous period
        compare = compare_prev_period;
      }
      const datePickerDate = getDatePickerUIObjectFromDateRange({
        range,
        compare,
        rangeTitle: DATE_PICKER_RANGE_TITLES.CUSTOM,
        compareTitle,
        compare_prev_period,
        compare_last_year
      });
      callback(datePickerDate);
      this.storeDatePickerDate(datePickerDate);
      window.$bus.trigger(EVENTS.FLOATING_COMPOUND_TABLE_OPEN_COLLAPSE);
    },
    getEvolutionsForField(fieldObject, options = {
      hasCurrencyEvolution: false,
      suffixes: {}
    }) {
      const {
        key,
        label
      } = fieldObject;
      if (!("key" in fieldObject) && !("label" in fieldObject)) return {};
      const prevSuffix = options?.suffixes?.prev_period || null;
      const lastYearSuffix = options?.suffixes?.same_period_ly || null;
      const prevEvolKey = prevSuffix ? `${key}_evol${prevSuffix}` : `${key}_evol`;
      const lastYearEvolKey = lastYearSuffix ? `${key}_evol${lastYearSuffix}` : `last_year_${key}_evol`;
      const fields = {};

      /*
        Build evolution fields in the required order:
        1. Previous period percentage evolution
        2. Previous period currency evolution (if enabled)
        3. Same period last year percentage evolution
        4. Same period last year currency evolution (if enabled)
      */
      fields[`${label} Previous Period Evolution`] = prevEvolKey;
      if (options.hasCurrencyEvolution) {
        fields[`${label} Previous Period Currency Evolution`] = `${key}_evol_currency`;
      }
      fields[`${label} Same Period Last Year Evolution`] = lastYearEvolKey;
      if (options.hasCurrencyEvolution) {
        fields[`${label} Same Period Last Year Currency Evolution`] = `last_year_${key}_evol_currency`;
      }
      return fields;
    },
    toggleEvolutionType(e, type) {
      e.stopPropagation();
      this.evolutionType = type;
    },
    async searchAndSelect({
      entity,
      // Filter Instance
      keyword,
      // Array
      key,
      filters = {},
      postActions = [],
      eventTrigger = null
    }) {
      // Clear and apply filters on the selected entity
      entity.clear();
      entity.apply();
      let res;
      if (Array.isArray(keyword) && keyword.length === 0) {
        res = await entity.asyncSearch({
          keyword,
          args: filters,
          key
        });
      } else {
        // Perform search with the given keyword and filters
        res = await entity.asyncSearchAndSelect({
          keyword,
          args: filters,
          key
        });
      }

      // Exit if search result is empty
      if (!res) {
        window.$bus.trigger("showDanger", {
          message: `Could not search for the ${keyword}`,
          dismissSecs: 3,
          visible: true
        });
        throw Error(`Could not search for the ${keyword}`);
      }

      // Re-apply the filters to the entity if needed
      entity.apply();

      // Trigger any additional actions specified in postActions
      postActions.forEach(action => action());

      // Optionally trigger a custom event if specified
      if (eventTrigger) {
        window.$bus.trigger(eventTrigger.name, eventTrigger.payload);
      }
    },
    async clearAndRefresh({
      entity,
      filters = {},
      postActions = []
    }) {
      // Clear the entity after performing an async search with the specified filters
      await entity.clearAfterAsyncSearch({
        args: filters
      });

      // Trigger any additional post-actions specified
      postActions.forEach(action => action());
    },
    trackCsvExport(module) {
      const event_name = `${module}CsvExported`;
      window.$bus.trigger(EVENTS.MIXPANEL_TRACK, {
        event_name: event_name,
        properties: {}
      });
    },
    trackDashboardCsvExport() {
      this.trackCsvExport("dashboard");
    },
    trackAdvertisingCsvExport() {
      this.trackCsvExport("advertising");
    },
    handleDatePickerChange(val) {},
    storeDatePickerDate(date) {
      try {
        if (!this.datePickerCommitterFunction) throw new Error();
        this.datePickerCommitterFunction(date);
        window.$bus.trigger(EVENTS.RESET_DATE_PICKER, date);
        window.$bus.trigger(EVENTS.SETUP_DATE_PICKER);
      } catch (err) {
        console.warn("DatePicker Committer Function is missing.");
      }
    },
    applyPeriodForDatePicker(periodGroupBy, periodCategory) {
      const compareTitle = this.datePickerCompareTitle(periodCategory);
      const date = getDatePickerUIObjectFromDateRange({
        ...getDatePickerRangeCompare(compareTitle, periodGroupBy, this.getSystemWeekType),
        rangeTitle: periodGroupBy,
        compareTitle
      });
      this.storeDatePickerDate(date);
      return date;
    },
    applyPeriodCategoryForDatePicker(datePickerDate, periodCategory) {
      const compareTitle = this.datePickerCompareTitle(periodCategory);
      const rangeTitle = datePickerDate.rangeTitle;
      let rangeAndCompare;

      // When range is custom with actual values, auto-compute compare values
      if (rangeTitle === DATE_PICKER_RANGE_TITLES.CUSTOM && datePickerDate.range.start && datePickerDate.range.end) {
        const customComputeFn = CUSTOM_COMPARE_FUNCTION[compareTitle];
        if (customComputeFn) {
          rangeAndCompare = customComputeFn({
            start: moment(datePickerDate.range.start),
            end: moment(datePickerDate.range.end),
            externalPeriod: periodCategory
          });
        } else {
          rangeAndCompare = getDatePickerRangeCompare(compareTitle, rangeTitle, this.getSystemWeekType);
        }
      } else {
        rangeAndCompare = getDatePickerRangeCompare(compareTitle, rangeTitle, this.getSystemWeekType);
      }
      const date = getDatePickerUIObjectFromDateRange({
        ...rangeAndCompare,
        rangeTitle,
        compareTitle
      });
      this.storeDatePickerDate(date);
      return date;
    }
  }
};